import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';
import * as Sentry from '@sentry/react';

import {
  CardContainer,
  CardWithModal,
  ICardItem,
  PhoneNumberInputWithConfirm,
  AddressInputWithConfirm
} from 'shared-components';

import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import * as PIActions from '../../../../store/student/personalInfo/actions';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { toggleTaskMenu } from '../../../../store/ui/actions';

import { Q } from '../../../../store/student/types';
import { ConfigField, IPhone } from '../../../../types';
import { Pages } from '../../../../store/student/personalInfo/types';

import QuestionPage from '../../../templates/question';
import { getPageStatus, Visible } from './utils';
import { titleAppend } from '../../../../utilities/head';

const getPhoneNumber = (phone?: string): IPhone => {
  if (phone && phone.split('-').length) {
    return {
      code: phone ? phone.split('-')[0] : null,
      phone: phone ? phone.split('-')[1] : null
    };
  }
  return {
    code: null,
    phone: phone || null
  };
};

const PersonalQPage3 = () => {
  // Sentry.captureException(new Error('test error - something went wrong'));

  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = (config: any) => {
    dispatch(StudentActions.savePersonalInfo(Pages.PAGE3, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = useCallback(
    (fieldName: string, value: any, refValue?: any) =>
      dispatch(PIActions.createUpdatePIFieldAction(fieldName, value, refValue)),
    [dispatch]
  );
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };
  const sectionTitle = 'Personal Information';

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const config = useSelector(({ config }: ApplicationState) => config, shallowEqual);

  const importData = useSelector(({ student }: ApplicationState) => student.importData, shallowEqual);

  const isPageComplete = useSelector(({ student }: ApplicationState) => student.offer.isPIPage3Complete, shallowEqual);

  const {
    PhoneNumber: phoneNumberConfig,
    HomeAddress: homeAddressConfig,
    City: cityConfig,
    Postcode: postCodeConfig,
    Country: countryConfig
  }: {
    PhoneNumber: ConfigField;
    HomeAddress: ConfigField;
    City: ConfigField;
    Postcode: ConfigField;
    Country: ConfigField;
  } = config.Checklist.PersonalInfo.Page3;

  const {
    phoneNumber = phoneNumberConfig?.defaultValue,
    homeAddress = homeAddressConfig?.defaultValue,
    city = cityConfig?.defaultValue,
    postCode = postCodeConfig?.defaultValue,
    country = countryConfig?.defaultValue
  } = student.personalInfo;

  // constants
  const currentStep = 3;
  const steps = 10;

  const [complete, setComplete] = useState(false);
  const [visible, setVisible] = useState<Visible | undefined>();
  const [courseName, setCourseName] = useState(student.offer?.courses?.[0]?.courseName || '');

  useEffect(() => {
    if ((student?.offer?.courses?.length ?? 0) > 0 && student.offer.courses![0]?.courseName) {
      setCourseName(student.offer.courses![0].courseName);
    }
  }, [student?.offer?.courses]);

  const [isPhoneValid, setPhoneValid] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [hasAddFirstButton, setHasAddFirstButton] = useState(
    importData.city &&
      importData.country &&
      importData.postcode &&
      (importData.addressLine1 || importData.addressLine2 || importData.addressLine3)
      ? ''
      : 'Add Your Residential Address'
  );

  const getImportDataHomeAddress = (): string => {
    let homeAddress = importData.addressLine1 ? `${importData.addressLine1}` : '';
    if (importData.addressLine2) {
      homeAddress += ` ${importData.addressLine2}`;
    }
    if (importData.addressLine3) {
      homeAddress += ` ${importData.addressLine3}`;
    }
    return homeAddress.trim();
  };

  const [importApproved, setImportApproved] = useState<string[]>([]);
  const [importUpdateEnabled, setImportUpdateEnabled] = useState<string[]>([]);
  const [importEdited, setImportEdited] = useState<string[]>([]);
  const [phone, setPhoneNumber] = useState<IPhone>(getPhoneNumber(phoneNumber));

  useEffect(() => {
    if (isPhoneValid) {
      setPhoneNumber(getPhoneNumber(phoneNumber));
    }
  }, [phoneNumber, isPhoneValid]);

  const [address, setAddress] = useState({
    city: city ? city : importData?.city ?? '',
    country: country ? country : importData?.country ?? '',
    postCode: postCode ? postCode : importData?.postcode ?? '',
    homeAddress: homeAddress
      ? homeAddress
      : importData?.addressLine1 || importData?.addressLine2 || importData?.addressLine3
      ? getImportDataHomeAddress()
      : ''
  });

  useEffect(
    () => {
      setImportApproved(getImportApprovedFields());
      setImportUpdateEnabled(getImportUpdateFields());
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [importData, isPageComplete]
  );

  useEffect(
    () => {
      const { complete: pageComplete, visible: pageVisible } = getPageStatus(config, {
        phoneNumber,
        homeAddress,
        city,
        country,
        postCode
      });
      setComplete(pageComplete);
      setVisible(pageVisible);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phoneNumber, homeAddress, city, country, postCode]
  );

  const toggleModal = (value: boolean) => {
    setShowModal(value);
  };

  const getImportApprovedFields = (): Array<string> => {
    const importApproved = [];

    const importHomeAddress = getImportDataHomeAddress();
    if (
      isPageComplete &&
      (importData.city === address.city || (importData.city === '' && address.city !== '')) &&
      (importData.country === address.country || (importData.country === '' && address.country !== '')) &&
      importData.postcode === address.postCode &&
      (importHomeAddress === address.homeAddress || (importHomeAddress === '' && address.homeAddress !== ''))
    ) {
      importApproved.push(Q.ADDRESS);
    }

    return importApproved;
  };

  const getImportUpdateFields = (): Array<string> => {
    const importUpdateEnabled = [];

    if (
      isPageComplete &&
      ((importData.city && importData.city !== address.city) ||
        (importData.country && importData.country !== address.country) ||
        (importData.postcode && importData.postcode !== address.postCode) ||
        ((importData.addressLine1 || importData.addressLine2 || importData.addressLine3) &&
          getImportDataHomeAddress() !== address.homeAddress))
    ) {
      importUpdateEnabled.push(Q.ADDRESS);
    }
    return importUpdateEnabled;
  };

  const isImportEnabled = (target: string): boolean => {
    if (target.includes(Q.ADDRESS)) {
      return (
        !!importData.city ||
        !!importData.country ||
        !!importData.postcode ||
        !!importData.addressLine1 ||
        !!importData.addressLine2 ||
        !!importData.addressLine3
      );
    }
    return false;
  };

  const isImportUpdateEnabled = (target: string): boolean => {
    if (!isPageComplete) return false;
    if (target.includes(Q.ADDRESS)) {
      return importUpdateEnabled.includes(target);
    }

    return false;
  };

  const handleImportEdit = (target: string): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited([...importEdited, target]);
  };

  const handleImportApprove = (target: string): void => {
    setImportApproved([...importApproved, target]);
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const isImportComplete = (target: string): boolean => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return importApproved.includes(target);
  };

  const getComplete = (): boolean => {
    const importQuestions: string[] = [Q.PHONE_NUMBER, Q.ADDRESS];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    const { code, phone: newPhone } = phone;

    return complete && importComplete && isPhoneValid && `${code || ''}` !== '' && `${newPhone || ''}` !== '';
  };

  const getCardItems = () => {
    const cardItems = [];
    if (visible?.homeAddress) {
      cardItems.push({
        label: 'Address',
        value: homeAddress ? homeAddress : getImportDataHomeAddress(),
        required: homeAddressConfig?.mandatory
      });
    }
    if (visible?.city) {
      cardItems.push({
        label: 'City',
        value: city ? city : importData.city,
        required: cityConfig?.mandatory
      });
    }
    if (visible?.postCode) {
      cardItems.push({
        label: 'Postcode',
        value: postCode ? postCode : importData.postcode,
        required: postCodeConfig?.mandatory
      });
    }
    if (visible?.country) {
      cardItems.push({
        label: 'Country',
        value: country ? country : importData.country,
        required: countryConfig?.mandatory
      });
    }
    return cardItems;
  };

  const renderCard = () => {
    const cardItems = getCardItems();
    const hasData = cardItems.some((cardItem: ICardItem) => cardItem.value !== undefined && cardItem.value !== '');
    return (
      <CardWithModal
        id={`address`}
        index={1}
        onRemove={() => {
          onChange(Q.CITY, '');
          onChange(Q.HOME_ADDRESS, '');
          onChange(Q.POSTCODE, '');
          onChange(Q.COUNTRY, '');
          setAddress({
            city: '',
            country: '',
            postCode: '',
            homeAddress: ''
          });
          setHasAddFirstButton('Add Your Residential Address');
        }}
        modalTitle={'Add a Permanent Address'}
        hasInCardActions={!importData.city && !importData.country && !getImportDataHomeAddress() && !!cardItems.length}
        cardItems={cardItems}
        hasAddMoreButton={false}
        showModal={showModal}
        hasAddFirstButton={!hasData ? hasAddFirstButton : undefined}
        toggleModal={(index: number) => {
          toggleModal(!showModal);
        }}
        importEnabled={isImportEnabled(Q.ADDRESS)}
        importUpdateEnabled={isImportUpdateEnabled(Q.ADDRESS)}
        importApproved={importApproved.includes(Q.ADDRESS)}
        importEdited={importEdited.includes(Q.ADDRESS)}
        onImportApprove={() => {
          handleImportApprove(Q.ADDRESS);
          const isEdited =
            importEdited.includes(Q.ADDRESS) ||
            ((address.city || !cityConfig?.mandatory) &&
              (address.country || !countryConfig?.mandatory) &&
              (address.postCode || !postCodeConfig?.mandatory) &&
              (address.homeAddress || !homeAddressConfig?.mandatory) &&
              (importData?.city !== address.city ||
                importData?.country !== address.country ||
                importData?.postcode !== address.postCode ||
                getImportDataHomeAddress() !== address.homeAddress));

          onChange(Q.CITY, isEdited ? address.city : importData?.city);
          onChange(Q.HOME_ADDRESS, isEdited ? address.homeAddress : getImportDataHomeAddress());
          onChange(Q.POSTCODE, isEdited ? address.postCode : importData?.postcode);
          onChange(Q.COUNTRY, isEdited ? address.country : importData?.country);
          if (!isEdited) {
            setAddress({
              city: importData?.city,
              country: importData?.country,
              postCode: importData?.postcode,
              homeAddress: getImportDataHomeAddress()
            });
          }
        }}
      >
        <AddressInputWithConfirm
          id={Q.ADDRESS}
          addressLabel={homeAddressConfig?.label}
          addressSubText={homeAddressConfig?.subLabel}
          cityLabel={cityConfig?.label}
          cleanSuggestionsAfterSave={true}
          postcodeLabel={postCodeConfig?.label}
          addressValue={address.homeAddress}
          cityValue={address.city}
          postcodeValue={address.postCode}
          countryLabel={countryConfig?.label}
          countryValue={address.country}
          hasSaveButton={true}
          complete={
            address.homeAddress &&
            (postCodeConfig?.mandatory ? address.postCode : true) &&
            address.country &&
            address.city
          }
          onChangeAddress={(event: any) => {
            setAddress({
              ...address,
              ...{ homeAddress: event.currentTarget.value }
            });
          }}
          onChangeCity={(event: any) => {
            setAddress({
              ...address,
              ...{ city: event.currentTarget.value }
            });
          }}
          onChangePostcode={(event: any) => {
            setAddress({
              ...address,
              ...{ postCode: event.currentTarget.value }
            });
          }}
          onChangeCountry={(value: string) => {
            setAddress({
              ...address,
              ...{ country: value }
            });
          }}
          // inputHelperText={homeAddressConfig?.helpText}
          onSaveAndClose={(course: any) => {
            if (homeAddress) {
              setHasAddFirstButton('');
            }
            onChange(Q.COUNTRY, address.country, courseName);
            onChange(Q.POSTCODE, address.postCode);
            onChange(Q.CITY, address.city);
            onChange(Q.HOME_ADDRESS, address.homeAddress);
            handleImportEdit(Q.ADDRESS);
            setImportApproved(getImportApprovedFields());
            setImportUpdateEnabled(getImportUpdateFields());
            toggleModal(false);
          }}
        />
      </CardWithModal>
    );
  };

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {visible?.phoneNumber && (
        <PhoneNumberInputWithConfirm
          key={Q.PHONE_NUMBER}
          id={Q.PHONE_NUMBER}
          autoFocus={true}
          label={phoneNumberConfig?.label}
          subText={phoneNumberConfig?.subLabel}
          value={`+${phone.code}${phone.phone}`}
          codeValue={phone.code ? phone.code : ''}
          numberValue={phone.phone ? phone.phone : ''}
          inputHelperText={phoneNumberConfig?.helpText}
          onValidationFailed={() => {
            setPhoneValid(false);
            // onChange(Q.PHONE_NUMBER, ''); // removed ENR-530
          }}
          onValidationSuccess={() => {
            const { code, phone } = phoneNumber;
            if (!isPhoneValid) {
              setPhoneValid(true);
            }

            const { code: oldCode, phone: oldPhone } = getPhoneNumber(phone);

            if (`${oldCode || ''}-${oldPhone || ''}` !== `${code || ''}-${phone || ''}`) {
              onChange(Q.PHONE_NUMBER, `${code || ''}-${phone || ''}`);
            }
          }}
          onChangeCode={(event: React.FormEvent<HTMLInputElement>) => {
            const code = event.currentTarget.value;
            onChange(Q.PHONE_NUMBER, `${code || ''}-${phone.phone || ''}`);
            setPhoneNumber({
              ...phone,
              code
            });
          }}
          onChangeNumber={(event: React.FormEvent<HTMLInputElement>) => {
            const phoneNumber = event.currentTarget.value;
            onChange(Q.PHONE_NUMBER, `${phone.code || ''}-${phoneNumber || ''}`);
            setPhoneNumber({
              ...phone,
              ...{ phone: phoneNumber }
            });
          }}
        />
      )}
      <CardContainer title={'Permanent Residential Address'}>{renderCard()}</CardContainer>
    </QuestionPage>
  );
};

export default PersonalQPage3;
